// ⚠️ FICHIER GÉNÉRÉ — ne pas éditer ici. Source : shared/ui/ · régénérer avec `bash shared/sync.sh`
'use client';

/* ═══════════════════════════════════════════════════════════════════════════
 * ON NE QUITTE UNE FENÊTRE QUE PAR LA CROIX — règle générale du 04/09/2026
 *
 * ── La demande, telle qu'elle a été posée ──────────────────────────────────
 *
 * « Si une fenêtre est un pop-up, le fait de cliquer en dehors du pop-up ne
 * permet pas de quitter la fenêtre sans sauvegarde. Pour quitter le pop-up il
 * faut clairement appuyer sur le X et si des modifications ne sont pas
 * enregistrées, une alerte surgit. »
 *
 * Elle vaut pour LES TROIS applications — d'où ce module dans le socle partagé
 * plutôt qu'un mécanisme par module. Le module « échange inter-laboratoires »
 * (`eil`) n'a aucune fenêtre modale et est de toute façon hors de cette
 * synchronisation (voir shared/sync.sh).
 *
 * ── Ce que cette règle REMPLACE ────────────────────────────────────────────
 *
 * Le 01/09/2026, la protection du voile avait été posée en OPT-IN. Trois jours
 * plus tard, sur cinquante-huit fenêtres, UNE SEULE était déclarée protégée.
 * Un dispositif qu'il faut penser à activer écran par écran ne protège que
 * l'écran dont on se souvient. La règle s'inverse : le clic à côté ne referme
 * plus rien, nulle part, et il n'y a pas d'exception à déclarer.
 *
 * ── Trois décisions qui font la différence entre une garde et une gêne ─────
 *
 * · LE CLIC À CÔTÉ NE DEMANDE MÊME PAS CONFIRMATION. Il n'est pas un geste :
 *   il n'y a rien à viser à côté d'une fenêtre. Poser une question sur un
 *   geste involontaire apprend en deux jours à répondre « oui » sans lire.
 *   Il est simplement INERTE — mais jamais muet : la boîte tressaille et la
 *   croix s'éclaire. On ne s'explique pas, on montre où est la sortie.
 *
 * · ÉCHAP RESTE UNE SORTIE, par la même porte que la croix, alerte comprise.
 *   C'est un geste volontaire — on ne touche pas cette touche par accident —
 *   et la supprimer enfermerait qui navigue au clavier dans une fenêtre.
 *
 * · L'ALERTE NE SE DÉCLARE PAS, elle se CONSTATE. Le hook écoute les
 *   événements de saisie émis dans la boîte et ne retient que ceux dont
 *   `isTrusted` est vrai — produits par un humain, jamais par le code. Une
 *   valeur qui arrive du serveur après l'ouverture ne marque donc rien, là où
 *   une comparaison de valeurs l'aurait comptée comme une saisie.
 *
 * ⚠️ CE QUE LE HOOK NE PEUT PAS DEVINER : qu'une saisie vient d'être
 * ENREGISTRÉE. Il faut donc le lui DIRE, par `saisieEnregistree()`.
 *
 * Le raisonnement du 04/09 — « un bouton Enregistrer appelle la fermeture
 * directement, et la fermeture directe ne traverse jamais la garde » — ne vaut
 * que pour les fenêtres qui se referment en enregistrant. Toutes ne le font
 * pas : le dossier d'une personne repasse en LECTURE et reste ouvert, si bien
 * qu'on continue de le consulter après avoir enregistré. La frappe restait
 * comptée, et la croix posait ensuite une question sur une saisie qui n'existe
 * plus. Signalé par la RQ le 06/09/2026 sur une fiche de personnel.
 *
 * ⚠️ Une alerte qui se pose alors qu'il n'y a RIEN à perdre est pire que pas
 * d'alerte du tout : elle apprend à répondre « quitter » sans lire, et le jour
 * où la question porte sur un vrai travail, elle n'est plus lue non plus.
 *
 * ⚠️ Cet oubli est DÉCLARÉ, comme le reste du module : le hook constate qu'on
 * a tapé, il ne peut pas constater qu'un serveur a répondu. À n'appeler
 * qu'APRÈS un enregistrement réussi — l'appeler avant, ou sur un échec,
 * effacerait la garde sur une saisie encore à sauver.
 *
 * ── `sansSaisie` : la consultation garde son confort (arbitrage du 04/09) ──
 *
 * La règle a été posée pour ce qui s'ENREGISTRE. La RQ a tranché le même jour
 * sur les fenêtres qui ne font que MONTRER — un journal, un aperçu, une fiche
 * liée : « garder également le confort quand c'est purement consultatif ». Le
 * clic à côté y referme donc, comme avant.
 *
 * ⚠️ `sansSaisie` commande DEUX choses à la fois, et c'est voulu : le voile
 * redevient une sortie, et l'alerte ne se pose plus. Les séparer laisserait
 * exister une fenêtre où le clic à côté referme une saisie sans rien demander
 * — précisément le défaut que la règle corrige. Une fenêtre est consultative
 * ou elle ne l'est pas.
 *
 * ⚠️ Et c'est bien une DÉCLARATION, jamais une déduction : le hook constate
 * qu'on a tapé, il ne peut pas savoir que ce qu'on tape mérite d'être gardé.
 * Un filtre ou un tri réglé par un `<select>` dans une fenêtre de lecture
 * compterait sinon comme une saisie, et l'alerte se tromperait — une alerte
 * qui se trompe est une alerte qu'on cesse de lire.
 * ═══════════════════════════════════════════════════════════════════════════ */

import React from 'react';

/** Les éléments dont une modification compte comme une saisie. */
const SAISIES = 'input, textarea, select, [contenteditable=""], [contenteditable="true"]';

/** La classe portée par la boîte quand on a cliqué à côté. Les trois modules
 *  définissent les images-clés sous ce nom — voir leur feuille globale. */
export const CLASSE_REFUS = 'bio24-fenetre-refus';

/**
 * Vrai dès que l'utilisateur a modifié un champ à l'intérieur de `boite`.
 *
 * Rend des fonctions de LECTURE et d'OUBLI, et non un état : la valeur n'est
 * consultée qu'au moment où l'on tente de sortir, et la faire vivre dans un
 * `useState` ferait re-rendre la fenêtre entière à la première frappe de chaque
 * champ.
 *
 * `oublier` repart de zéro — à appeler quand la saisie a été ENREGISTRÉE, seul
 * fait que l'écoute des événements ne peut pas observer.
 */
export function useSaisieTouchee(boite: React.RefObject<HTMLElement | null>, actif = true) {
  const touchee = React.useRef(false);
  React.useEffect(() => {
    if (!actif) return undefined;
    const el = boite.current;
    if (!el) return undefined;
    touchee.current = false;
    const noter = (e: Event) => {
      if (!e.isTrusted) return;
      const cible = e.target as HTMLElement | null;
      if (!cible || !cible.matches?.(SAISIES)) return;
      touchee.current = true;
    };
    /* En capture : un champ qui arrête la propagation de son `change` — le cas
       de plusieurs sélecteurs maison — passerait sinon inaperçu. */
    el.addEventListener('input', noter, true);
    el.addEventListener('change', noter, true);
    return () => {
      el.removeEventListener('input', noter, true);
      el.removeEventListener('change', noter, true);
    };
  }, [actif, boite]);
  const lire = React.useCallback(() => touchee.current, []);
  const oublier = React.useCallback(() => { touchee.current = false; }, []);
  /* Objet MÉMORISÉ : reconstruit à chaque rendu, il ferait de `demanderSortie`
     une dépendance neuve à chaque fois, et l'écoute d'Échap se réabonnerait à
     chaque frappe. */
  return React.useMemo(() => ({ lire, oublier }), [lire, oublier]);
}

/**
 * La sortie d'une fenêtre : ce qu'on pose sur le voile, sur la boîte, et la
 * porte unique par laquelle la croix et Échap passent.
 *
 * ⚠️ `demanderSortie` est la SEULE porte. Brancher la croix sur `onClose` en
 * plus est exactement ainsi que la croix avait survécu à la protection du voile
 * dans la version du 01/09 : trois sorties, deux gardées.
 */
export function useSortieFenetre({ boite, onClose, sansSaisie = false, actif = true }: {
  boite: React.RefObject<HTMLElement | null>;
  onClose: () => void;
  sansSaisie?: boolean;
  actif?: boolean;
}) {
  const saisie = useSaisieTouchee(boite, actif && !sansSaisie);
  const [alerte, setAlerte] = React.useState(false);
  /* Compteur et non booléen : deux clics à côté doivent tressaillir deux fois,
     ce qu'un booléen déjà vrai ne ferait pas. */
  const [refus, setRefus] = React.useState(0);

  const demanderSortie = React.useCallback(() => {
    if (saisie.lire()) { setAlerte(true); return; }
    onClose();
  }, [onClose, saisie]);

  React.useEffect(() => {
    if (!actif) return undefined;
    const surTouche = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      /* Tant que l'alerte est posée, elle seule écoute Échap : sinon la même
         frappe répondrait à la question ET refermerait la fenêtre. */
      if (alerte) { setAlerte(false); return; }
      demanderSortie();
    };
    window.addEventListener('keydown', surTouche);
    return () => window.removeEventListener('keydown', surTouche);
  }, [actif, alerte, demanderSortie]);

  return {
    /**
     * À étaler sur le VOILE.
     * · fenêtre de saisie   → ne ferme rien, signale seulement le refus ;
     * · fenêtre consultative → referme, comme avant (arbitrage du 04/09/2026).
     */
    proprietesVoile: {
      onMouseDown: sansSaisie ? onClose : () => setRefus((n) => n + 1),
    },
    /** À étaler sur la BOÎTE (après son propre `style`, qu'il complète). */
    proprietesBoite: {
      className: refus ? CLASSE_REFUS : undefined,
      /* Le nom de l'animation alterne : rejouer une animation déjà jouée sur le
         même élément ne produit RIEN si son nom ne change pas — le second clic
         à côté serait resté sans réponse. */
      style: refus ? { animationName: `bio24-refus-${refus % 2}` } : undefined,
      onMouseDown: (e: React.MouseEvent) => e.stopPropagation(),
    },
    /** Vrai quand on vient de cliquer à côté — pour éclairer la croix. */
    refuse: refus > 0,
    /** La porte unique : à brancher sur la croix. */
    demanderSortie,
    /**
     * « C'EST ENREGISTRÉ » — à appeler après un enregistrement RÉUSSI, dans les
     * fenêtres qui restent ouvertes ensuite. Sans cet appel, la croix
     * demanderait de confirmer l'abandon d'une saisie déjà sauvée.
     * Les fenêtres qui se referment en enregistrant n'ont rien à appeler.
     */
    saisieEnregistree: saisie.oublier,
    /** L'alerte est-elle posée ? */
    alerte,
    rester: React.useCallback(() => setAlerte(false), []),
    quitterSansEnregistrer: React.useCallback(() => { setAlerte(false); onClose(); }, [onClose]),
  };
}

/**
 * LE VOILE SEUL — pour les fenêtres qui n'ont rien à enregistrer.
 *
 * Un aperçu, un journal, une fiche que l'on consulte : la sortie reste la
 * croix, mais il n'y a pas de saisie à protéger, donc pas d'alerte à poser.
 * ⚠️ Ce n'est PAS une dispense de la règle : le clic à côté reste inerte, et il
 * tressaille de la même façon. C'est la seule chose que ce hook fait.
 */
export function useRefusVoile() {
  const [refus, setRefus] = React.useState(0);
  return {
    proprietesVoile: { onMouseDown: () => setRefus((n) => n + 1) },
    proprietesBoite: {
      className: refus ? CLASSE_REFUS : undefined,
      style: refus ? { animationName: `bio24-refus-${refus % 2}` } : undefined,
      onMouseDown: (e: React.MouseEvent) => e.stopPropagation(),
    },
    refuse: refus > 0,
  };
}

/**
 * L'ALERTE DE SORTIE — posée SUR la fenêtre qu'elle concerne.
 *
 * ⚠️ Le bouton par défaut, celui que le focus prend et qu'Entrée active, est
 * « Rester » : la question naît d'un geste dont on n'est pas certain qu'il ait
 * été voulu, et le défaut d'une question ambiguë doit être ce qui ne détruit
 * rien. L'autre bouton NOMME ce qu'il fait — « Quitter sans enregistrer », et
 * non « Confirmer », qui ne dit pas ce qui sera perdu.
 *
 * ⚠️ Habillée avec les SEULS jetons présents dans les trois modules
 * (`--surface-card`, `--text-strong`, `--text-muted`, `--status-warning`,
 * `--border-default`) : ce composant est rendu dans les trois, et un jeton
 * absent d'un module y peindrait la couleur héritée sans rien signaler.
 *
 * ⚠️ `position: absolute` — la boîte parente doit donc porter `position:
 * relative`. Rendue en `fixed`, l'alerte couvrirait aussi ce qui est SOUS la
 * fenêtre, et l'on ne verrait plus de quelle saisie il est question.
 */
export function AlerteSortie({ onRester, onQuitter }: { onRester: () => void; onQuitter: () => void }) {
  const bouton = React.useRef<HTMLButtonElement | null>(null);
  React.useEffect(() => { bouton.current?.focus(); }, []);
  const btn = (fort: boolean): React.CSSProperties => ({
    font: 'inherit', fontSize: 13, fontWeight: 600, padding: '7px 13px', borderRadius: 8, cursor: 'pointer',
    border: '1px solid var(--border-default)',
    background: fort ? 'var(--text-strong)' : 'var(--surface-card)',
    color: fort ? 'var(--surface-card)' : 'var(--text-strong)',
  });
  return (
    <div role="alertdialog" aria-modal="true" aria-label="Modifications non enregistrées"
      style={{
        position: 'absolute', inset: 0, background: 'rgba(11,20,38,0.45)', display: 'flex',
        alignItems: 'center', justifyContent: 'center', padding: 18,
        /* ⚠️ 10, pas 1 (bug relevé le 13/09/2026) : une fenêtre qui contient un
           tableau (`TableauTriable`, en-tête figé à `zIndex: 2`) passait CE
           tableau devant l'alerte — la boîte « Rester / Quitter sans
           enregistrer » se retrouvait coupée en deux par l'en-tête collant.
           10 la place au-dessus de tout ce qu'une fenêtre peut contenir en
           interne, sans sortir de la boîte : elle reste `position: absolute`,
           bornée par le `position: relative` du parent. */
        zIndex: 10,
      }}>
      <div style={{
        background: 'var(--surface-card)', border: '1px solid var(--border-default)', borderRadius: 12,
        boxShadow: '0 8px 40px rgba(14,39,64,0.25)', maxWidth: 380, padding: 18,
      }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--status-warning)' }}>Modifications non enregistrées</div>
        <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '6px 0 0', lineHeight: 1.55 }}>
          Ce que vous venez de saisir dans cette fenêtre sera perdu si vous la fermez maintenant.
        </p>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
          <button ref={bouton} type="button" style={btn(true)} onClick={onRester}>Rester</button>
          <button type="button" style={btn(false)} onClick={onQuitter}>Quitter sans enregistrer</button>
        </div>
      </div>
    </div>
  );
}
